import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  inject,
} from '@angular/core'
import { LoggerFactory } from '@elderbyte/ts-logger'
import { BehaviorSubject, Observable } from 'rxjs'
import { ConstraintSpec } from '../constraint-spec'
import { ActivatedRoute, Router } from '@angular/router'
import {
  ElderFormFieldDenseDirective,
  ElderHeaderComponent,
  ElderSelectComponent,
  ElderSelectValueDirective,
  ElderThemeDirective,
  ElderToastService,
  SelectionModelPopupDirective,
} from '@elderbyte/ngx-starter'
import { Property, PropertyKind } from '../../../../properties/property'
import { PropertyService } from '../../../../properties/property.service'
import { IdValidator } from '../../../../../features/validators/id-validator'
import { PropertyUtil } from '../../../../properties/property-util'
import { ElderTranslatePipe } from '@elderbyte/ngx-starter'
import { MatInput } from '@angular/material/input'
import { MatCheckbox } from '@angular/material/checkbox'
import { ConstraintOperatorSelectDirective } from '../constraint-operator-select/constraint-operator-select.directive'
import { PropertyBrowserComponent } from '../../../../properties/property-browser/property-browser.component'
import { PropertySelectDirective } from '../../../../properties/property-select/property-select.directive'
import { MatFormField, MatLabel } from '@angular/material/form-field'
import { FormsModule } from '@angular/forms'
import { MatIcon } from '@angular/material/icon'
import { MatIconButton } from '@angular/material/button'
import { MatToolbar } from '@angular/material/toolbar'
import { AsyncPipe } from '@angular/common'

@Component({
  selector: 'app-constraint-spec-detail',
  templateUrl: './constraint-spec-detail.component.html',
  styleUrls: ['./constraint-spec-detail.component.scss'],
  imports: [
    MatToolbar,
    ElderThemeDirective,
    MatIconButton,
    MatIcon,
    ElderHeaderComponent,
    FormsModule,
    MatFormField,
    ElderFormFieldDenseDirective,
    MatLabel,
    ElderSelectComponent,
    PropertySelectDirective,
    ElderSelectValueDirective,
    SelectionModelPopupDirective,
    PropertyBrowserComponent,
    ConstraintOperatorSelectDirective,
    MatCheckbox,
    MatInput,
    AsyncPipe,
    ElderTranslatePipe,
  ],
})
export class ConstraintSpecDetailComponent implements OnInit {
  /***************************************************************************
   *                                                                         *
   * Fields                                                                  *
   *                                                                         *
   **************************************************************************/

  private readonly _constraintSpec$: BehaviorSubject<ConstraintSpec> = new BehaviorSubject(null)

  public readonly PropertyKind = PropertyKind

  private _creationMode: boolean

  @Input()
  public embedded = false

  @Output()
  public close: EventEmitter<Event> = new EventEmitter<Event>()

  @Output()
  public save: EventEmitter<Event> = new EventEmitter<Event>()

  public readonly idValidatorPattern = IdValidator.pattern

  public PropertyUtil = PropertyUtil
  private productPropertyService = inject(PropertyService)

  /***************************************************************************
   *                                                                         *
   * Life Cycle                                                              *
   *                                                                         *
   **************************************************************************/

  public ngOnInit(): void {
    if (!this.constraintSpec) {
      this.initNew()
    }
  }

  /***************************************************************************
   *                                                                         *
   * Getter / Setters                                                        *
   *                                                                         *
   **************************************************************************/

  public get constraintSpec$(): Observable<ConstraintSpec> {
    return this._constraintSpec$.asObservable()
  }

  public get constraintSpec(): ConstraintSpec {
    return this._constraintSpec$.getValue()
  }

  @Input()
  public set constraintSpec(spec: ConstraintSpec) {
    if (spec) {
      this._creationMode = !spec.property
      this._constraintSpec$.next(spec)
    }
  }

  public get creationMode(): boolean {
    return this._creationMode
  }

  public get deletionEnabled(): boolean {
    return !this.creationMode
  }

  /***************************************************************************
   *                                                                         *
   * Component Events                                                        *
   *                                                                         *
   **************************************************************************/

  public onSave(event: Event): void {
    this.emitSave(event)
  }

  public onClose(event: Event): void {
    this.emitClose(event)
  }

  public productPropertySelectionChanged(productProperty: Property): void {
    if (productProperty) {
      this.constraintSpec.propertyId = productProperty?.id
      this.updateConstraintOperator(productProperty)
    }
  }

  /***************************************************************************
   *                                                                         *
   * Private Methods                                                         *
   *                                                                         *
   **************************************************************************/

  private emitSave(event?: Event): void {
    this.save.emit(event)
  }

  private emitClose(event?: Event): void {
    this.close.emit(event)
  }

  private initNew(): ConstraintSpec {
    return new ConstraintSpec()
  }

  private updateConstraintOperator(property: Property): void {
    const availableOps = this.productPropertyService.getAvailableOperators(property)
    this.constraintSpec.operator = availableOps.includes(this.constraintSpec.operator)
      ? this.constraintSpec.operator // Current operator also works with updated product property
      : availableOps[0] // Current operator is not supported with updated product property -> set valid operator
  }
}
